import { FC, Suspense, useMemo, memo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Alert, Stack, Typography } from '@mui/material';
import {
  chatSelectors,
  HeroConversation,
  useAppSelector,
  useGetActiveHeroConversations,
  useGetArchivedHeroConversations,
  useGetAssociateSettings,
} from '@vs-online-only-app/state';
import { CenteredLoader, useResizable } from '@vs-online-only-app/ui';
import ActiveConversationsList from './conversations-list/ActiveConversationsList';
import ArchivedConversationsList from './conversations-list/ArchivedConversationsList';
import { ConversationTypeMenuTab } from './ConversationTypeMenuTab';

const resizeHandleStyles = css<{ $isHoveringEdge?: boolean }>`
  &:after {
    content: '';
    position: absolute;
    z-index: 100;
    top: 0;
    right: 0;
    width: 2px;
    height: 100%;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.palette.info.main};
    opacity: ${({ $isHoveringEdge }) => ($isHoveringEdge ? 0.3 : 0)};
    transition: opacity 0.2s ease;
    transition-delay: 0.5s;
  }
`;

const Container = styled(Stack)<{ $isHoveringEdge?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(2)};
  overflow: auto;
  position: relative;

  ${resizeHandleStyles}
`;

const AllConversationsSection: FC = () => {
  const selectedChatType = useAppSelector(chatSelectors.selectSelectedChatType);
  const { data: associateSettings } = useGetAssociateSettings();
  const { data: activeConversations = [] } = useGetActiveHeroConversations();
  const {
    data: archivedConversationPages,
    isFetching,
    fetchNextPage,
  } = useGetArchivedHeroConversations();

  const containerRef = useRef<HTMLDivElement>(null);
  const { isHoveringEdge } = useResizable(containerRef);

  const archivedConversations = useMemo(() => {
    const uniqueConversations = new Map<string, HeroConversation>();
    archivedConversationPages?.pages.forEach((page) => {
      page.conversations.forEach((conversation) => {
        uniqueConversations.set(conversation.id, conversation);
      });
    });
    const archivedConversations = Array.from(uniqueConversations.values());

    // Get ids of active conversations
    const activeConversationsIds = activeConversations.map(({ id }) => id);
    // Filter out active conversations from archived conversations
    const filteredArchivedConversations = archivedConversations.filter(
      ({ id }) => !activeConversationsIds.includes(id),
    );

    return filteredArchivedConversations;
  }, [activeConversations, archivedConversationPages?.pages]);

  const maximumChats = associateSettings?.settings.maximumChats;

  const activeConversationsCount = useMemo(() => {
    if (!activeConversations) {
      return 0;
    }
    const uniqueConversations = new Set(
      activeConversations.map((conversation) => conversation.id),
    );
    return uniqueConversations.size;
  }, [activeConversations]);

  const remainingChatsCount = useMemo(
    () =>
      maximumChats && activeConversationsCount !== undefined
        ? maximumChats - activeConversationsCount
        : null,
    [maximumChats, activeConversationsCount],
  );

  const remainingChatsCountMessage = useMemo(() => {
    if (selectedChatType === 'archived') {
      return;
    }
    if (remainingChatsCount === 0) {
      return <>You're in the maximum number of chats.</>;
    }
    if (remainingChatsCount) {
      if (activeConversationsCount) {
        return (
          <>
            Claim up to <strong>{remainingChatsCount}</strong>{' '}
            {remainingChatsCount === 1 ? 'more chat.' : 'more chats.'}
          </>
        );
      }
      return (
        <>
          Claim up to <strong>{remainingChatsCount}</strong>{' '}
          {remainingChatsCount === 1 ? 'chat.' : 'chats.'}
        </>
      );
    }
  }, [activeConversationsCount, remainingChatsCount, selectedChatType]);

  return (
    <Container ref={containerRef} $isHoveringEdge={isHoveringEdge}>
      <Typography variant="h6">All chats</Typography>
      <Suspense fallback={null}>
        <ConversationTypeMenuTab
          activeConversationsCount={activeConversations?.length}
          archivedConversationsCount={archivedConversations.length}
        />
      </Suspense>
      <Stack height="100%" overflow="auto">
        {remainingChatsCountMessage && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {remainingChatsCountMessage}
          </Alert>
        )}
        <Suspense fallback={<CenteredLoader />}>
          {selectedChatType === 'active' ? (
            <ActiveConversationsList
              activeConversations={activeConversations}
            />
          ) : (
            <ArchivedConversationsList
              archivedConversations={archivedConversations}
              isFetching={isFetching}
              fetchNextPage={fetchNextPage}
            />
          )}
        </Suspense>
      </Stack>
    </Container>
  );
};

export default memo(AllConversationsSection);
